import { DateTime } from 'luxon';

import Exception from '@/common/models/Exception';
import { DASH, generateUUID } from '@/globals';
import formatNumber from '@/utils/NumberFormatter';

export const UserNotificationType = Object.freeze({
  info: 'info',
  success: 'success',
  error: 'error',
  warning: 'warning',
});

export default class UserNotification {
  id: string;
  type: typeof UserNotificationType;
  title: string;
  message: string;
  link: { label: string; route: string; callback: () => void };
  duration?: number;
  details?: { label: string; modalTitle: string; content: string };
  actions: Array<{ label: string; isDismissingNotification: boolean; fn: () => void }>;
  category: string;
  displayedAt: DateTime<true>;
  isHidden: boolean;
  reverseProgressPercentage: number;
  counter: number;

  constructor(options) {
    const validProps = [
      'type',
      'title',
      'message',
      'link', // { label, route, callback }
      'duration',
      'details', // { label, content } (can be used to pass details about an error for example.)
      'actions', // [{ label, isDismissingNotification, fn }]
      'category',
    ];

    const invalidProperties = Object.keys(options).filter((x) => !validProps.includes(x));

    if (invalidProperties.length > 0) {
      throw new Exception(`Invalid properties: ${invalidProperties.join(', ')}`);
    }

    //
    // Validate the link if specified
    //
    if (options.link != null) {
      const validLinkProps = ['label', 'route', 'callback'];
      const invalidLinkProperties = Object.keys(options.link).filter((x) => !validLinkProps.includes(x));

      if (invalidLinkProperties.length > 0) {
        throw new Exception(`Invalid properties for 'link': ${invalidLinkProperties.join(', ')}`);
      }
    }

    this.id = generateUUID();
    this.type = options.type;
    this.title = options.title;
    this.message = options.message;
    this.link = options.link;
    this.duration = options.duration || 5000;
    this.details = options.details || null;
    this.actions = options.actions || null;
    this.category = options.category || null;
    this.displayedAt = DateTime.local();
    this.isHidden = false;
    this.reverseProgressPercentage = 0;
  }

  hide() {
    this.isHidden = true;
  }

  incrementCounter() {
    this.counter += 1;
  }

  get hasDurationEllapsed() {
    return this.displayedDuration > this.duration;
  }

  get displayedDuration() {
    return DateTime.local().diff(this.displayedAt, 'milliseconds').milliseconds;
  }

  get progressPercentageFormatted() {
    if (this.duration === 0) return DASH;
    return formatNumber(this.displayedDuration / this.duration, '0%');
  }

  get progressPercentage() {
    if (this.duration === 0) return 0;
    return Math.round(100 * (this.displayedDuration / this.duration));
  }
}
